import { MutableRefObject, useEffect } from 'react';
import { INote, IGenerateRequest, IResponseForm } from '../global/interfaces';
import {
  IBooking,
  IDelta,
  IForm,
  IUserDocumentSection,
  ITranscriptSegment,
  IImage,
} from '@homeporter/types';
import { deepClone, getWholeTranscript, uuid } from '../utils/helpers';
import toggleToast from '../utils/messaging';

// this assumes the response is a json array with some leading and trailing characters, use parseValidFormBlocks for chunked response and incomplete json ersponse
const extractJsonBlock = (str: string) => {
  const match = str.match(/```json\s*([\s\S]*?)\s*```/);
  if (!match) throw new Error('No JSON block found');
  return match[1];
};

const safelyParseJsonArray = (str: string) => {
  return extractJsonBlock(str)
    .trim()
    .replace(/,\s*{[^}]*$/, ''); // remove last incomplete object if any
};

const extractJsonArray = (str: string) => {
  try {
    let jsonString = '';
    const match = str.match(/\[\s*{[\s\S]*}\s*\]/);
    if (match) {
      jsonString = match[0];
    } else {
      jsonString = safelyParseJsonArray(str);
    }

    return JSON.parse(jsonString);
  } catch (err) {
    console.error('Failed to parse JSON:', err);
    return null;
  }
};

interface UseProcessFormProps {
  booking: IBooking | null;
  documentSection: IUserDocumentSection | null;
  context: string;
  transcriptSegments: ITranscriptSegment[];
  transcriptAttachments: Record<string, any[]>; // TODO: Add proper type
  setGenerateRequest: (request: any) => void; // TODO: Add proper type
  setBookingContext: (booking: IBooking, context: string) => void;
  setSaveEnabled: (enabled: boolean) => void;
  regenerateIndex: number | undefined;
  setRegenerateIndex: (index: number | undefined) => void;
  responseForm: IResponseForm | null;
  setDocumentSection: (section: IUserDocumentSection | null) => void;
  setTranscriptSegments: (segments: any[]) => void;
  setScrollTo: (scrollTo: string | undefined) => void;
  resetAttachments: (
    bookingId: number | undefined,
    transcriptAttachments: Record<string, (IDelta | INote)[]>,
  ) => void;
  generateRequest: IGenerateRequest | undefined;
  imagesMapRef: MutableRefObject<Map<string, IImage> | null>;
}

export const useProcessForm = ({
  booking,
  documentSection,
  context,
  transcriptSegments,
  transcriptAttachments,
  setGenerateRequest,
  setBookingContext,
  setSaveEnabled,
  regenerateIndex,
  setRegenerateIndex,
  responseForm,
  setDocumentSection,
  setScrollTo,
  resetAttachments,
  generateRequest,
  imagesMapRef,
}: UseProcessFormProps) => {
  useEffect(() => {
    console.log(':::::regenerateIndex:', regenerateIndex);
  }, [regenerateIndex]);

  useEffect(() => {
    const formCard =
      regenerateIndex !== undefined
        ? (documentSection?.cards[regenerateIndex] as IForm)
        : undefined;

    if (!documentSection || !booking || formCard?.type !== 'Form') {
      if (!formCard) setGenerateRequest(undefined);
      return;
    }

    if (transcriptSegments.length === 0) {
      toggleToast('Transcript is empty', 'error');
      setGenerateRequest(undefined);
      setRegenerateIndex(undefined);
      return;
    }

    console.log(':::::regenerateIndex form card:', regenerateIndex);

    const _context = formCard.context || documentSection.context || context;

    if (!context) setBookingContext(booking, _context);
    if (!documentSection.context) documentSection.context = _context;
    if (!formCard.context) formCard.context = _context;

    const setCriteria = (
      idx: number,
      iidx: number,
      title: string,
      subtitle: string,
      label: string,
      criteria: string,
    ) => {
      //      return `[qid:${idx}${iidx > -1 ? `.${iidx}` : ''}] ${title ? `Title: "${title}" ` : ''}${subtitle ? `- Criterion: "${subtitle}", ` : ''}${label ? `"${label}", ` : ''}${criteria ? `"${criteria}", ` : ''}`;
      return `[qid:${idx}${iidx > -1 ? `.${iidx}` : ''}] ${subtitle ? `Title: "${subtitle}"` : ''} - Criterion: ${label ? `"${label}", ` : ''}${criteria ? `"${criteria}", ` : ''}`;
    };

    const criteriaForm: string[] = [];
    formCard.data.forEach((sectionEntry) => {
      sectionEntry.children.forEach((child) => {
        const items = child.items;
        for (const item of items) {
          item.asids = undefined;
          item.usids = undefined;
          item.air = undefined;
          item.uir = undefined;

          if (Array.isArray(item.type)) {
            item.type.forEach((type, index) => {
              criteriaForm.push(
                `${setCriteria(item.idx, index, sectionEntry.title, child.subtitle, item.label, item.criteria)} - Return true ONLY if ${type} is correct.`,
              );
            });
          } else {
            criteriaForm.push(
              item.type === 'boolean'
                ? `${setCriteria(item.idx, -1, sectionEntry.title, child.subtitle, item.label, item.criteria)} - Return true if confirmed, false if contradicted.`
                : `${setCriteria(item.idx, -1, sectionEntry.title, child.subtitle, item.label, item.criteria)} - Return value as type ${item.type} only if stated.`,
            );
          }
        }
      });
    });

    const { parts, hasImageDescriptions } = getWholeTranscript(
      transcriptSegments,
      transcriptAttachments,
      imagesMapRef,
    );

    const chunks = [{ sentences: parts.join('\n'), sids: [] }];

    formCard.id = uuid();
    documentSection.cards[regenerateIndex || -1] = deepClone(formCard);
    console.log('::::setDocument section 4...');
    setDocumentSection({ ...documentSection });
    setGenerateRequest(undefined);

    setGenerateRequest({
      bookingId: booking.booking_id,
      regenerateIndex,
      algorithm: formCard.algorithm.toLowerCase().replaceAll(' ', '-'),
      type: 'Form',
      chunks,
      index: 0,
      criteria: criteriaForm.join('\n'),
      context: _context,
      temperature: formCard.temperature,
    });
  }, [regenerateIndex]);

  useEffect(() => {
    if (
      !responseForm ||
      !documentSection ||
      regenerateIndex === undefined ||
      generateRequest?.chunks === undefined ||
      generateRequest.index === undefined
    )
      return;

    const formCard = documentSection.cards[
      generateRequest.regenerateIndex
    ] as IForm;

    const responseJson = extractJsonArray(responseForm.text);
    if (responseJson) {
      const responseSet =
        responseJson?.reduce((acc: { [key: string]: any }, item: any) => {
          if (item.qid) {
            acc[item.qid] = item;
          }
          return acc;
        }, {}) || {};

      console.log(':::::responseJson:', responseJson);
      console.log(':::::criteriaSet:', responseSet);
      console.log(':::::responseForm:', responseForm);
      console.log(':::::formCard:', formCard);

      formCard.data.forEach((sectionEntry) => {
        sectionEntry.children.forEach((child) => {
          const items = child.items;

          for (const item of items) {
            if (Array.isArray(item.type)) {
              item.type.forEach((type, index) => {
                if (!item.air) item.air = '';
                item.air += responseSet[`${item.idx}.${index}`]?.extracted
                  ? '1'
                  : '0';

                if (responseSet[`${item.idx}.${index}`]?.sids) {
                  if (!Array.isArray(item.asids)) {
                    item.asids = responseSet[`${item.idx}.${index}`].sids;
                  } else {
                    item.asids.concat(responseSet[`${item.idx}.${index}`].sids);
                  }
                }
              });
            } else {
              if (responseSet[item.idx]) {
                item.air =
                  item.type === 'boolean' &&
                  typeof responseSet[item.idx].extracted === 'boolean'
                    ? responseSet[item.idx].extracted === true
                      ? '1'
                      : '0'
                    : responseSet[item.idx].extracted;
                item.asids = responseSet[item.idx].sids;
              }
            }
          }
        });
      });
    } else {
      toggleToast('Invalid response format, please try again.', 'error');
    }

    if (responseForm.message_id) {
      formCard.message_id = responseForm.message_id;
    }

    formCard.id = uuid();
    documentSection.cards[generateRequest.regenerateIndex] =
      deepClone(formCard);
    console.log('::::setDocument section 5...');
    setDocumentSection({ ...documentSection });
    setSaveEnabled(true);
    if (generateRequest.index < generateRequest.chunks?.length - 1 || 0) {
      console.log(':::::generateRequest.index:', generateRequest.index);

      setGenerateRequest({
        ...generateRequest,
        index: generateRequest.index + 1,
      });
    } else {
      setScrollTo(undefined);
      setGenerateRequest(undefined);
    }
  }, [responseForm]);
};
